import type { CarouselRenderProgress } from "@/lib/api";

const DEFAULT_MS = 18_000;

function elapsedMs(iso?: string, now = Date.now()): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, now - t);
}

export function etaLabel(sec: number): string {
  const s = Math.max(0, Math.round(sec || 0));
  if (s >= 90) {
    const mins = Math.floor(s / 60);
    const rest = s % 60;
    return rest ? `${mins} min ${String(rest).padStart(2, "0")} s` : `${mins} min`;
  }
  return `${s} s`;
}

export function estimateRenderProgress(
  progress: CarouselRenderProgress | null | undefined,
  extras?: { done?: number; total?: number; phase?: string },
  now = Date.now(),
): { percent: number; eta_sec: number; ms_per_slide: number; elapsed_sec: number } {
  const total = Math.max(1, Number(extras?.total || progress?.total || 1));
  const done = Math.max(0, Math.min(Number(extras?.done ?? progress?.done ?? 0), total));
  const phase = extras?.phase || progress?.phase || "rendering";
  const expected = Math.max(4000, Number(progress?.expected_ms_per_slide || DEFAULT_MS));
  const elapsed = elapsedMs(progress?.started_at, now);
  const current = elapsedMs(progress?.slide_started_at, now);
  const elapsedSec = Math.max(0, Math.round(elapsed / 1000));
  const jobMs = expected * total;
  const timePct = jobMs ? Math.min(92, Math.max(1, (100 * elapsed) / jobMs)) : 1;
  const timeEta = Math.max(0, (jobMs - elapsed) / 1000);
  if (phase === "done" || done >= total) {
    return { percent: 100, eta_sec: 0, ms_per_slide: expected, elapsed_sec: elapsedSec };
  }
  if (phase === "queued" || phase === "waiting") {
    return {
      percent: Math.max(1, Math.round(timePct)),
      eta_sec: Math.round(timeEta),
      ms_per_slide: expected,
      elapsed_sec: elapsedSec,
    };
  }
  const completedMs = Math.max(0, elapsed - current);
  let msPer = expected;
  if (done >= 1 && completedMs >= 400) {
    const live = completedMs / done;
    msPer = done < 2 ? (live + expected) / 2 : live;
  }
  msPer = Math.max(3000, msPer);
  const frac = done < total ? Math.min(0.92, current / msPer) : 0;
  const effective = done + frac;
  const slidePct = Math.min(99, Math.max(1, (100 * effective) / total));
  const slideEta = ((total - effective) * msPer) / 1000;
  const percent = Math.min(99, Math.max(timePct, slidePct));
  const eta = done >= 1 ? Math.min(timeEta, slideEta) : timeEta;
  return {
    percent: Math.max(1, Math.round(percent)),
    eta_sec: Math.max(0, Math.round(eta)),
    ms_per_slide: Math.round(msPer),
    elapsed_sec: elapsedSec,
  };
}
